import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useSite, UI, resolveImg } from "@/lib/site";
import { MaskLine, Reveal } from "@/components/Reveal";
import { SocialButtons } from "@/components/WaButton";

const StarCard = ({ item, i }) => {
  const { t } = useSite();
  const note = t(item.note);
  return (
    <motion.figure
      className="group relative h-full overflow-hidden rounded-[20px] bg-[#171310]"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 5) * 0.06 }}
      data-testid={`star-card-${i}`}
    >
      <div className="relative aspect-[3/4] h-full overflow-hidden">
        <img src={resolveImg(item.image)} alt={item.name || "Dari Event"} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[50%_30%] grayscale-[35%] transition-[transform,filter] [transition-duration:1200ms] group-hover:scale-[1.06] group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
        <span className="absolute left-4 top-4 font-display text-lg text-[#f0d78e]">{String(i + 1).padStart(2, "0")}</span>
      </div>
      {(item.name || note) && (
        <figcaption className="absolute inset-x-0 bottom-0 p-4">
          {item.name && <p className="font-display text-xl leading-tight text-[#fbf3e2] md:text-2xl">{item.name}</p>}
          {note && <p className="mt-1 text-xs text-[#e2d6c0]">{note}</p>}
        </figcaption>
      )}
    </motion.figure>
  );
};

export const Stars = ({ s }) => {
  const { t } = useSite();
  const items = s.items.filter((x) => x?.image);
  const n = items.length;
  const smSpan = { 0: "sm:col-span-3", 1: "sm:col-span-2", 2: "sm:col-span-1" }[n % 3];
  const lgSpan = { 0: "lg:col-span-5", 1: "lg:col-span-4", 2: "lg:col-span-3", 3: "lg:col-span-2", 4: "lg:col-span-1" }[n % 5];
  return (
    <section data-testid="stars-section" className="grain relative overflow-hidden bg-[#0e0c0a] px-5 py-24 text-[#f6ecd9] md:px-10 md:py-36">
      <div className="relative mx-auto max-w-[1400px]">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow !text-[#e6ca7a]">{t(UI.nav.booking)}</p>
            <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl"><MaskLine inView>{t(s.title)}</MaskLine></h2>
          </div>
          <Reveal className="lg:col-span-5"><p className="text-base leading-relaxed text-[#d8ccb6] md:text-lg">{t(s.lead)}</p></Reveal>
        </div>
        <div className="mt-16 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((it, i) => <StarCard key={i} item={it} i={i} />)}
          <div className={`relative flex flex-col justify-between overflow-hidden rounded-[20px] border border-[#e6ca7a]/30 bg-gradient-to-br from-[#2a2015] to-[#0e0c0a] p-7 ${n % 2 ? "col-span-1" : "col-span-2"} ${smSpan} ${lgSpan}`} data-testid="stars-outro">
            <Sparkles className="h-8 w-8 text-[#f0d78e]" strokeWidth={1.2} />
            <div className="mt-8">
              <p className="font-display gold-text text-3xl leading-tight md:text-4xl">{t(s.outroTitle)}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#d8ccb6] md:text-base">{t(s.outroText)}</p>
              <SocialButtons label={t(UI.learnMore)} className="mt-6" testPrefix="stars" dark />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
